import { Injectable, Injector, signal } from '@angular/core';
import {
  PatientBehaviorAntecedentModel,
  PatientBehaviorConsequenceModel,
  PatientBehaviorDataModel,
  PatientBehaviorLocationModel,
  PatientBehaviorModel,
  PatientBehaviorResponseModel,
  PatientBehaviorSeverityModel,
  PatientSessionModel,
  PatientTargetDataModel,
  PatientTargetModel,
  SkillAreaModel,
  PatientProgramModel,
  PatientPlaylistModel,
  PromptScheduleModel,
  PatientTargetStatus,
  TargetType,
  PatientReminderModel,
  PatientProgramStatus,
  PromptCodeModel,
} from '../../core/api/models';
import { StaffApiService } from '../../core/api/services';
import { firstValueFrom, from, map, Observable, of, tap } from 'rxjs';
import { AlertService } from '../../shared/services/alert.service';
import { Router } from '@angular/router';
import {
  OfflineDataService,
  PatientOfflineData,
} from '../../core/services/offline-data.service';
import { SupervisorApiService } from '../../core/api/services';
import { AuthService } from '../../auth/auth.service';
import { PatientDataFacadeService } from '../../core/services/patient-data-facade.service';
import { SyncSchedulerService } from '../../core/services/sync-scheduler.service';
import '../../shared/utils/utils';

@Injectable({
  providedIn: 'root',
})
export class PatientSessionService {
  private currentPatientID: number | null = null;
  private currentSessionID: number | null = null;
  private transientPreviewSession: PatientSessionModel | null = null;
  private targetsRunningDataCache: {
    SessionID: number;
    TargetID: number;
    Data: any;
  }[] = [];
  private runningTimerKeys = new Set<string>();
  private syncSchedulerServiceInstance: SyncSchedulerService | null = null;

  public dataChanged = signal<number>(0);

  constructor(
    private authService: AuthService,
    private staffApiService: StaffApiService,
    private alertService: AlertService,
    private router: Router,
    private offlineDataService: OfflineDataService,
    private supervisorApiService: SupervisorApiService,
    private patientDataFacade: PatientDataFacadeService,
    private injector: Injector
  ) {
    console.log(
      'PatientSessionService constructor called',
      new Date().getTime()
    );

    // Load running data from localStorage
    const targetsRunningData = localStorage.getItem('targetsRunningData');
    if (targetsRunningData) {
      this.targetsRunningDataCache = JSON.parse(targetsRunningData);
      this.refreshRunningTimersFromCache();
    }
    window.addEventListener('sparkSessionIDReconciled', (event: Event) => {
      const detail = (event as CustomEvent<{
        oldSessionID: number;
        newSessionID: number;
      }>).detail;
      this.targetsRunningDataCache.forEach((item) => {
        if (item.SessionID === detail.oldSessionID) {
          item.SessionID = detail.newSessionID;
        }
      });
      if (this.currentSessionID === detail.oldSessionID) {
        this.currentSessionID = detail.newSessionID;
      }
      this.refreshRunningTimersFromCache();
      this.dataChanged.update((value) => value + 1);
    });
  }

  private get syncSchedulerService(): SyncSchedulerService {
    if (!this.syncSchedulerServiceInstance) {
      this.syncSchedulerServiceInstance = this.injector.get(SyncSchedulerService);
    }
    return this.syncSchedulerServiceInstance;
  }

  private markDirtyForSync(reason: string): void {
    this.syncSchedulerService.markDirty(reason);
  }

  private flushDirtyForSync(reason: string): Promise<boolean> {
    return firstValueFrom(this.syncSchedulerService.flushNow(reason));
  }

  registerRunningTimer(sessionID: number, targetID: number): void {
    this.runningTimerKeys.add(this.getRunningTimerKey(sessionID, targetID));
  }

  unregisterRunningTimer(sessionID: number, targetID: number): void {
    this.runningTimerKeys.delete(this.getRunningTimerKey(sessionID, targetID));
  }

  hasRunningTimers(): boolean {
    this.refreshRunningTimersFromCache();
    return this.runningTimerKeys.size > 0;
  }

  private getRunningTimerKey(sessionID: number, targetID: number): string {
    return `${sessionID}:${targetID}`;
  }

  private refreshRunningTimersFromCache(): void {
    this.runningTimerKeys.clear();
    for (const runningData of this.targetsRunningDataCache) {
      if (runningData.Data?.isStarted) {
        this.registerRunningTimer(
          runningData.SessionID,
          runningData.TargetID
        );
      }
    }
  }

  // Get current patient ID
  getPatientID(): number | null {
    if (this.currentPatientID) {
      return this.currentPatientID;
    }

    const patientIDFromLocalStorage = localStorage.getItem('patientID');
    if (patientIDFromLocalStorage) {
      this.currentPatientID = parseInt(patientIDFromLocalStorage);
      return this.currentPatientID;
    }

    return null;
  }

  // For API calls that expect a number
  private getPatientIDForAPI(): number | undefined {
    const id = this.getPatientID();
    return id !== null ? id : undefined;
  }

  // Set current patient ID
  setPatientID(patientID: number | null): void {
    const previousPatientID = this.currentPatientID;
    const hasPatientChanged = previousPatientID !== patientID;

    if (hasPatientChanged && previousPatientID != null) {
      this.patientDataFacade.clearPatient(previousPatientID);
    }

    this.currentPatientID = patientID;
    if (patientID) {
      localStorage.setItem('patientID', patientID.toString());
    } else {
      localStorage.removeItem('patientID');
    }
    this.dataChanged.update((n) => n + 1);
  }

  activateSessionContext(
    patientID: number,
    sessionID: number,
    preservePreviousPatientData: boolean = false
  ): void {
    if (!preservePreviousPatientData) {
      this.setPatientID(patientID);
    } else {
      this.currentPatientID = patientID;
      localStorage.setItem('patientID', patientID.toString());
    }
    this.currentSessionID = sessionID;
    this.transientPreviewSession = null;
    this.dataChanged.update((n) => n + 1);
  }

  async startGroupSessions(
    members: { PatientID: number; SessionID: number }[]
  ): Promise<void> {
    const startedDate = new Date().toServerString();
    const saves: Promise<boolean | undefined>[] = [];

    for (const member of members) {
      const patientData = this.offlineDataService.getOfflinePatientData(
        member.PatientID
      );
      const session = patientData?.sessions.find(
        (item) => item.ID === member.SessionID
      );
      if (
        !session ||
        session.StartedDate ||
        session.FinishedDate ||
        session.DeletedDate
      ) {
        continue;
      }

      session.StartedDate = startedDate;
      saves.push(
        this.offlineDataService.saveOfflineSessionData(
          member.PatientID,
          session,
          true,
          false
        )
      );
    }

    await Promise.all(saves);
    if (saves.length > 0) {
      this.markDirtyForSync('group sessions started');
      this.dataChanged.update((n) => n + 1);
    }
  }

  // Get current patient session
  getPatientSession(): PatientSessionModel | null {
    const patientID = this.getPatientID();
    if (!patientID) {
      return null;
    }

    if (
      this.currentSessionID != null &&
      this.currentSessionID <= 0 &&
      this.transientPreviewSession
    ) {
      return this.transientPreviewSession.PatientID == null ||
        Number(this.transientPreviewSession.PatientID) === patientID
        ? this.transientPreviewSession
        : null;
    }

    const offlineData =
      this.offlineDataService.getOfflinePatientData(patientID);
    if (
      !offlineData ||
      !offlineData.sessions ||
      offlineData.sessions.length === 0
    ) {
      return null;
    }

    if (this.currentSessionID) {
      return (
        offlineData.sessions.find(
          (s) =>
            s.ID === this.currentSessionID &&
            (s.PatientID == null || Number(s.PatientID) === patientID)
        ) || null
      );
    }

    // Return the most recent real session and avoid selecting preview placeholder
    // sessions (ID <= 0) when currentSessionID is not set.
    const candidateSessions = offlineData.sessions.filter(
      (session) =>
        !!session.ID &&
        session.ID > 0 &&
        (session.PatientID == null || Number(session.PatientID) === patientID)
    );

    if (candidateSessions.length === 0) {
      return null;
    }

    return (
      [...candidateSessions].sort((a, b) => {
        // Handle potentially undefined dates by providing a default
        const dateA = a.StartedDate ? new Date(a.StartedDate) : new Date(0);
        const dateB = b.StartedDate ? new Date(b.StartedDate) : new Date(0);
        return dateB.getTime() - dateA.getTime();
      })[0] || null
    );
  }

  // Set current patient session
  setPatientSession(
    session: PatientSessionModel | null,
    isDirty: boolean = true,
    syncImmediately: boolean = true
  ): Promise<boolean | undefined> {
    if (!session) {
      this.currentSessionID = null;
      this.transientPreviewSession = null;
      return Promise.resolve(true);
    }

    this.currentSessionID = session.ID ?? null;
    const patientID = session.PatientID;
    if ((session.ID ?? 0) <= 0) {
      if (patientID) {
        this.setPatientID(patientID);
      }
      this.transientPreviewSession = { ...session };
      this.dataChanged.update((n) => n + 1);
      return Promise.resolve(true);
    }
    this.transientPreviewSession = null;

    if (patientID) {
      this.setPatientID(patientID);
      const save = this.offlineDataService
        .saveOfflineSessionData(
          patientID,
          session,
          isDirty,
          syncImmediately
        )
        .then(async (success) => {
          if (success && isDirty) {
            if (session.FinishedDate) {
              await this.flushDirtyForSync('patient session finished');
            } else {
              this.markDirtyForSync('patient session saved');
            }
          }
          this.dataChanged.update((n) => n + 1);
          return success;
        });
      this.dataChanged.update((n) => n + 1);
      return save;
    }
    return Promise.resolve(false);
  }

  setPatientSessionGuardianSignature(
    sessionID: number,
    guardianSignature: Blob
  ): void {
    const session = this.getPatientSession();
    if (!session) {
      return;
    }
    this.offlineDataService.saveSessionGuardianSignature(
      sessionID,
      guardianSignature
    );
    this.dataChanged.update((n) => n + 1);
  }

  setPatientSessionStaffHandoffSignature(
    sessionID: number,
    signature: Blob
  ): void {
    this.offlineDataService.saveSessionStaffHandoffSignature(
      sessionID,
      signature
    );
    this.dataChanged.update((n) => n + 1);
  }

  getPatientSessionStaffHandoffSignature(sessionID: number): Promise<Blob | null> {
    return this.offlineDataService.getSessionStaffHandoffSignature(sessionID);
  }

  // Get saved sessions
  getSavedSessions(): PatientSessionModel[] {
    const patientID = this.getPatientID();
    if (!patientID) {
      return [];
    }

    const offlineData =
      this.offlineDataService.getOfflinePatientData(patientID);
    if (!offlineData || !offlineData.sessions) {
      return [];
    }

    return offlineData.sessions.filter((s) => s.FinishedDate);
  }

  initializePatientSession(
    patientID?: number,
    sessionID?: number,
    trialCount?: number
  ): void {
    if (patientID) {
      this.setPatientID(patientID);
    }

    if (trialCount === undefined || !trialCount) trialCount = 1;
    else if (trialCount >= 3) {
      this.alertService.error('Failed to load patient data for offline use');
      console.error(
        `Error loading patient data for offline. Trial Count ${trialCount}.`
      );
      return;
    }
    if (sessionID) {
      this.currentSessionID = Number(sessionID);
    }

    const currentPatientID = this.getPatientID();
    if (!currentPatientID) {
      this.router.navigate(['/select-patient']);
      return;
    }

    // Ensure we have offline data for this patient
    if (!this.offlineDataService.hasOfflinePatientData(currentPatientID)) {
      // Check if we're actually in offline mode before trying to download
      if (this.offlineDataService.isInOfflineMode()) {
        // In offline mode, can't download - just update dataChanged to trigger UI refresh
        this.dataChanged.update((n) => n + 1);
        return;
      }

      // Try to save the patient data for offline use
      this.dataChanged.update((n) => n + 1);
      this.offlineDataService
        .savePatientForOffline(
          currentPatientID,
          sessionID == null,
          sessionID && sessionID > 0 ? sessionID : undefined
        )
        .subscribe({
          next: (success) => {
            if (!success) {
              this.alertService.error(
                'Failed to load patient data for offline use'
              );
            }
            this.dataChanged.update((n) => n + 1);
          },
          error: (error) => {
            console.error('Error loading patient data for offline:', error);
            this.alertService.error(
              'Failed to load patient data: ' + error.message
            );
          },
        });
    } else if (sessionID === -1) {
      // Create a new session
      const newSession: PatientSessionModel = {
        ID: -1,
        PatientID: currentPatientID,
        StartedDate: new Date().toServerString(),
        FinishedDate: null,
        TargetData: [],
        BehaviorData: [],
      };

      this.setPatientSession(newSession);
    } else if (sessionID) {
      // Load specific session
      const offlineData =
        this.offlineDataService.getOfflinePatientData(currentPatientID);
      if (offlineData) {
        const session = offlineData.sessions.find(
          (s) => s.ID?.toString() === sessionID.toString()
        );
        if (session) {
          if (
            session.StaffID &&
            session.StaffID != this.authService.userInfo?.ID &&
            !session.StaffIDs?.includes(this.authService.userInfo?.ID ?? 0)
          ) {
            this.alertService.error(
              'You are not authorized to access the session for Service #' +
                session.ServiceID +
                '. This service is assigned to another staff member.'
            );
            if (this.offlineDataService.isInOfflineMode()) {
              console.log('Navigating to offline mode from patient session service');
              this.router.navigate(['/offline/patients']);
            } else {
              this.router.navigate([
                '/staff/' + currentPatientID + '/sessions',
              ]);
            }
            return;
          }
          this.setPatientSession(session, false);
          this.dataChanged.update((n) => n + 1);
        } else {
          //Try to load the session from the server
          this.offlineDataService
            .savePatientForOffline(currentPatientID, false, sessionID)
            .subscribe({
              next: (success) => {
                if (success) {
                  this.initializePatientSession(
                    currentPatientID,
                    sessionID,
                    (trialCount ?? 0) + 1
                  );
                }
              },
              error: (error) => {
                console.error('Error loading patient data:', error);
                this.alertService.error(
                  'Failed to load patient data: ' + error.message
                );
              },
            });
        }
      }
    }
  }

  startSession(): void {
    const patientID = this.getPatientID();
    if (!patientID) {
      this.alertService.error('No patient selected');
      return;
    }

    const currentSession = this.getPatientSession();
    if (currentSession) {
      currentSession.StartedDate = new Date().toServerString();
      // Keep this local-first for fast UI response; sync can happen later.
      this.setPatientSession(currentSession, true, false);

      // Clear running data for the targets
      this.targetsRunningDataCache = [];
      this.runningTimerKeys.clear();
      localStorage.setItem(
        'targetsRunningData',
        JSON.stringify(this.targetsRunningDataCache)
      );
      this.dataChanged.update((n) => n + 1);
    }
  }

  reopenSession(sessionID: number): void {
    const patientID = this.getPatientID();
    if (!patientID) {
      this.alertService.error('No patient selected');
      return;
    }

    this.currentSessionID = sessionID;
    const currentSession = this.getPatientSession();
    if (currentSession) {
      currentSession.FinishedDate = null;
      this.setPatientSession(currentSession, true);

      // Clear running data for the targets
      this.targetsRunningDataCache = [];
      this.runningTimerKeys.clear();
      localStorage.setItem(
        'targetsRunningData',
        JSON.stringify(this.targetsRunningDataCache)
      );
      this.dataChanged.update((n) => n + 1);
    }
  }

  endSession(session: PatientSessionModel): Observable<any> {
    let currentSession: PatientSessionModel | null = session;
    if (!currentSession) {
      currentSession = this.getPatientSession();
    }
    if (!currentSession) {
      return of(null);
    }

    const patientID = this.getPatientID();
    if (!patientID) {
      return of(null);
    }

    // Mark the session as finished
    currentSession.FinishedDate = new Date().toServerString();

    //Check if there is any duration target or behvior that was started but not finished
    let startedTargets = this.targetsRunningDataCache.filter(
      (x) => x.SessionID == currentSession?.ID && x.Data.isStarted
    );
    for (let startedTarget of startedTargets) {
      if (startedTarget.Data.startTime) {
        const currentTime = Date.now();
        let timerValue = Math.floor(
          (currentTime - startedTarget.Data.startTime) / 1000
        );
        if (timerValue > 0) {
          let targetDataToSave: PatientTargetDataModel | null = null;
          let behaviorDataToSave: PatientBehaviorDataModel | null = null;

          if (
            startedTarget.Data.isIntervalTarget ||
            startedTarget.Data.isIntervalBehavior
          ) {
            // Incomplete whole-interval runs are not saved. Staff must let the
            // interval finish, or start it again after Stop.
            continue;
          } else if (startedTarget.Data.isFluencyRateTarget) {
            //Save fluency rate data, including zero-response timed runs.
            targetDataToSave = {
              PatientSessionID: currentSession.ID,
              IntValue: startedTarget.Data.totalCorrect ?? 0,
              TextValue: `${startedTarget.Data.totalCorrect ?? 0}/${startedTarget.Data.totalIncorrect ?? 0}`,
              Duration: timerValue,
              PatientTargetID: startedTarget.TargetID,
              RecordedDate: new Date().toServerString(),
            };
          } else if (startedTarget.Data.isRateTarget) {
            targetDataToSave = {
              PatientSessionID: currentSession.ID,
              IntValue: startedTarget.Data.totalCount ?? 0,
              TextValue: `${startedTarget.Data.totalCount ?? 0}`,
              Duration: Math.max(timerValue, 1),
              PatientTargetID: startedTarget.TargetID,
              RecordedDate: new Date().toServerString(),
            };
          } else if (startedTarget.Data.isToiletingTarget) {
            const duration = startedTarget.Data.isTimeUp
              ? startedTarget.Data.timerValue
              : startedTarget.Data.timerInitialValue -
                startedTarget.Data.timerValue;
            //Save toileting data
            targetDataToSave = {
              PatientTargetID: startedTarget.TargetID,
              PatientSessionID: currentSession.ID,
              RecordedDate: new Date().toServerString(),
              IntValue: duration,
              TextValue: duration.toString(),
              ToiletingData: {
                UrinationOn: startedTarget.Data.urinationOn,
                UrinationOff: startedTarget.Data.urinationOff,
                BowelMovementOn: startedTarget.Data.bowelMovementOn,
                BowelMovementOff: startedTarget.Data.bowelMovementOff,
                NoVoid: startedTarget.Data.noVoid,
                Duration: duration,
              },
            };
          } else if (startedTarget.Data.isDurationTarget) {
            const minutes = Math.floor(timerValue / 60);
            const seconds = timerValue % 60;
            const timerValueString = `${minutes
              .toString()
              .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;

            //Save duration data
            targetDataToSave = {
              PatientTargetID: startedTarget.TargetID,
              PatientSessionID: currentSession.ID,
              RecordedDate: new Date().toServerString(),
              IntValue: timerValue,
              TextValue: timerValueString,
            };
          } else if (startedTarget.Data.isDurationBehavior) {
            const minutes = Math.floor(timerValue / 60);
            const seconds = timerValue % 60;
            const timerValueString = `${minutes
              .toString()
              .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;

            //Save duration behavior data
            behaviorDataToSave = {
              PatientBehaviorID: startedTarget.TargetID - 900000000,
              PatientSessionID: currentSession.ID,
              RecordedDate: new Date().toServerString(),
              IntValue: timerValue,
              TextValue: timerValueString,
            };
          }

          if (targetDataToSave) {
            if (!currentSession.TargetData) {
              currentSession.TargetData = [];
            }
            currentSession.TargetData.push(targetDataToSave);
          }
          if (behaviorDataToSave) {
            if (!currentSession.BehaviorData) {
              currentSession.BehaviorData = [];
            }
            currentSession.BehaviorData.push(behaviorDataToSave);
          }
        }
      }
    }

    // Save the updated session
    const save = this.setPatientSession(currentSession, true);

    // Clear only this session's running data. Other group tabs may still have
    // active timers.
    this.currentSessionID = null;
    this.targetsRunningDataCache = this.targetsRunningDataCache.filter(
      (item) => item.SessionID !== currentSession?.ID
    );
    this.refreshRunningTimersFromCache();
    localStorage.setItem(
      'targetsRunningData',
      JSON.stringify(this.targetsRunningDataCache)
    );

    return from(save).pipe(
      map((success) => ({ success: !!success, offline: true }))
    );
  }

  getTargetData(targetID: number): PatientTargetDataModel[] | null {
    const currentSession = this.getPatientSession();
    if (!currentSession || !currentSession.TargetData) {
      return null;
    }

    return (
      currentSession.TargetData.filter((t) => t.PatientTargetID === targetID) ||
      null
    );
  }

  /**
   * Returns all target data entries for a given target across ALL sessions
   * stored in offline data. Used for targets (like Currency) whose balance
   * depends on the cumulative sum of all entries over time.
   */
  getAllTargetDataForTarget(targetID: number): PatientTargetDataModel[] {
    const patientID = this.getPatientID();
    if (!patientID) {
      return [];
    }

    const offlineData = this.offlineDataService.getOfflinePatientData(patientID);
    if (!offlineData || !offlineData.sessions || offlineData.sessions.length === 0) {
      return [];
    }

    const allTargetData: PatientTargetDataModel[] = [];

    for (const session of offlineData.sessions) {
      if (!session.TargetData) {
        continue;
      }

      for (const td of session.TargetData) {
        if (td.PatientTargetID === targetID && !td.DeletedDate) {
          allTargetData.push(td);
        }
      }
    }

    return allTargetData;
  }

  addTargetData(targetData: PatientTargetDataModel): void {
    const currentSession = this.getPatientSession();
    if (!currentSession) {
      return;
    }

    if (
      targetData.PatientSessionID == -1 ||
      currentSession.ID == -1 ||
      this.currentSessionID == -1
    ) {
      this.alertService.error(
        'This is just a preview of a session. Go to All Sessions to start or resume a session'
      );
      this.dataChanged.update((n) => n + 1);
      return;
    }

    const patientID = this.getPatientID();
    if (!patientID) {
      return;
    }

    if (!currentSession.TargetData) {
      currentSession.TargetData = [];
    }

    if (!targetData.PatientSessionID) {
      targetData.PatientSessionID = currentSession.ID;
    }

    if (!targetData.RecordedDate) {
      targetData.RecordedDate = new Date().toServerString();
    }

    // Check for duplicates before adding - particularly important for anecdotal targets
    const existingTargetDataIndex = currentSession.TargetData.findIndex(
      (t) =>
        t.PatientTargetID === targetData.PatientTargetID &&
        t.RecordedDate === targetData.RecordedDate
    );

    let currencyDelta = 0;
    if (existingTargetDataIndex !== -1) {
      // Update existing instead of adding a duplicate
      console.log(
        'Found existing target data with same date, updating instead of adding new'
      );
      const previous = currentSession.TargetData[existingTargetDataIndex];
      currencyDelta =
        (targetData.IntValue ?? 0) - (previous?.IntValue ?? 0);
      currentSession.TargetData[existingTargetDataIndex] = targetData;
    } else {
      // Add new target data
      currencyDelta = targetData.IntValue ?? 0;
      currentSession.TargetData.push(targetData);
    }

    if ((targetData.PatientTargetID ?? 0) > 0 && currencyDelta !== 0) {
      this.adjustCurrencyTargetCurrentBalance(
        patientID,
        targetData.PatientTargetID ?? 0,
        currencyDelta
      );
    }

    // Save the updated session
    this.offlineDataService
      .saveOfflineSessionData(patientID, currentSession)
      .then((success) => {
        if (success) {
          this.markDirtyForSync('target data added');
        }
        this.dataChanged.update((n) => n + 1);
      });
    this.dataChanged.update((n) => n + 1);
  }

  saveTargetData(targetData: PatientTargetDataModel): void {
    const currentSession = this.getPatientSession();
    if (!currentSession || !currentSession.TargetData) {
      return;
    }

    const patientID = this.getPatientID();
    if (!patientID) {
      return;
    }

    // For anecdotal targets, we need to be more careful about checking for duplicates
    // The primary key for matching should be PatientTargetID + RecordedDate
    const targetDataIndex = currentSession.TargetData.findIndex(
      (t) =>
        t.PatientTargetID === targetData.PatientTargetID &&
        ((!!t.ID && t.ID === targetData.ID) ||
          t.RecordedDate === targetData.RecordedDate)
    );

    if (targetDataIndex !== -1) {
      // Update existing target data
      const previous = currentSession.TargetData[targetDataIndex];
      const currencyDelta =
        (targetData.IntValue ?? 0) - (previous?.IntValue ?? 0);
      currentSession.TargetData[targetDataIndex] = targetData;

      if ((targetData.PatientTargetID ?? 0) > 0 && currencyDelta !== 0) {
        this.adjustCurrencyTargetCurrentBalance(
          patientID,
          targetData.PatientTargetID ?? 0,
          currencyDelta
        );
      }

      // Save the updated session
      this.offlineDataService
        .saveOfflineSessionData(patientID, currentSession)
        .then((success) => {
          if (success) {
            this.markDirtyForSync('target data saved');
          }
          this.dataChanged.update((n) => n + 1);
        });
      this.dataChanged.update((n) => n + 1);
    } else {
      // If we couldn't find it using the ID, try adding it
      this.addTargetData(targetData);
    }
  }

  private adjustCurrencyTargetCurrentBalance(
    patientID: number,
    patientTargetID: number,
    delta: number
  ): void {
    const offlineData = this.offlineDataService.getOfflinePatientData(patientID);
    if (!offlineData || !offlineData.targets || delta === 0) {
      return;
    }

    const target = offlineData.targets.find((t) => t.ID === patientTargetID);
    if (!target || target.TargetType !== TargetType.Currency) {
      return;
    }

    const current =
      target.CurrentBalance ?? target.StartingBalance ?? 0;
    target.CurrentBalance = current + delta;
    target.LastChangedDate = new Date().toISOString();
  }

  getTargetsRunningData(
    SessionID: number,
    targetID: number
  ): { SessionID: number; TargetID: number; Data: any } | null {
    const targetRunningDataFromLocalStorage =
      localStorage.getItem('targetsRunningData');
    if (targetRunningDataFromLocalStorage) {
      this.targetsRunningDataCache = JSON.parse(
        targetRunningDataFromLocalStorage
      );
    }
    const targetRunningData = this.targetsRunningDataCache.find(
      (t) => t.SessionID === SessionID && t.TargetID === targetID
    );
    return targetRunningData || null;
  }

  setTargetsRunningData(SessionID: number, targetID: number, data: any): void {
    const targetRunningDataFromLocalStorage =
      localStorage.getItem('targetsRunningData');
    if (targetRunningDataFromLocalStorage) {
      this.targetsRunningDataCache = JSON.parse(
        targetRunningDataFromLocalStorage
      );
    }
    const targetRunningData = this.targetsRunningDataCache.find(
      (t) => t.SessionID === SessionID && t.TargetID === targetID
    );
    const wasStarted = !!targetRunningData?.Data?.isStarted;
    const isStarted = !!data?.isStarted;
    const shouldNotifyTimerLifecycleChange =
      !targetRunningData || wasStarted !== isStarted;

    if (targetRunningData) {
      targetRunningData.Data = data;
    } else {
      this.targetsRunningDataCache.push({
        SessionID,
        TargetID: targetID,
        Data: data,
      });
    }

    localStorage.setItem(
      'targetsRunningData',
      JSON.stringify(this.targetsRunningDataCache)
    );
    if (data?.isStarted) {
      this.registerRunningTimer(SessionID, targetID);
    } else {
      this.unregisterRunningTimer(SessionID, targetID);
    }

    if (shouldNotifyTimerLifecycleChange) {
      this.dataChanged.update((n) => n + 1);
    }
  }

  claimIntervalCompletion(
    sessionID: number,
    targetID: number,
    startTime: number
  ): boolean {
    if (!sessionID || sessionID < 0 || !targetID || !startTime) {
      return false;
    }

    const running = this.getTargetsRunningData(sessionID, targetID);
    if (
      !running?.Data?.isStarted ||
      running.Data.startTime !== startTime ||
      running.Data.lastCompletedStartTime === startTime
    ) {
      return false;
    }

    this.setTargetsRunningData(sessionID, targetID, {
      ...running.Data,
      isStarted: false,
      lastCompletedStartTime: startTime,
    });
    return true;
  }

  getBehaviorData(targetID: number): PatientBehaviorDataModel[] | null {
    const currentSession = this.getPatientSession();
    if (!currentSession || !currentSession.BehaviorData) {
      return null;
    }

    return (
      currentSession.BehaviorData.filter(
        (t) => t.PatientBehaviorID === targetID
      ) || null
    );
  }

  addBehaviorData(behaviorData: PatientBehaviorDataModel): void {
    const currentSession = this.getPatientSession();
    if (!currentSession) {
      return;
    }

    if (
      behaviorData.PatientSessionID == -1 ||
      currentSession.ID == -1 ||
      this.currentSessionID == -1
    ) {
      this.alertService.error(
        'This is just a preview of a session. Go to All Sessions to start or resume a session'
      );
      this.dataChanged.update((n) => n + 1);
      return;
    }

    const patientID = this.getPatientID();
    if (!patientID) {
      return;
    }

    if (!currentSession.BehaviorData) {
      currentSession.BehaviorData = [];
    }

    if (!behaviorData.PatientSessionID) {
      behaviorData.PatientSessionID = currentSession.ID;
    }

    if (!behaviorData.RecordedDate) {
      behaviorData.RecordedDate = new Date().toServerString();
    }

    currentSession.BehaviorData.push(behaviorData);

    // Save the updated session
    this.offlineDataService
      .saveOfflineSessionData(patientID, currentSession)
      .then((success) => {
        if (success) {
          this.markDirtyForSync('behavior data added');
        }
        this.dataChanged.update((n) => n + 1);
      });
    this.dataChanged.update((n) => n + 1);
  }

  saveBehaviorData(behaviorData: PatientBehaviorDataModel, originalRecordedDate?: string): void {
    const currentSession = this.getPatientSession();
    if (!currentSession || !currentSession.BehaviorData) {
      return;
    }

    const patientID = this.getPatientID();
    if (!patientID) {
      return;
    }

    let behaviorDataIndex = currentSession.BehaviorData.findIndex(
      (t) => t === behaviorData
    );

    if (behaviorDataIndex === -1) {
      behaviorDataIndex = currentSession.BehaviorData.findIndex(
        (t) =>
          t.PatientBehaviorID === behaviorData.PatientBehaviorID &&
          ((!!t.ID && t.ID === behaviorData.ID) ||
            t.RecordedDate === behaviorData.RecordedDate ||
            (!!originalRecordedDate && t.RecordedDate === originalRecordedDate))
      );
    }

    if (behaviorDataIndex === -1) {
      const recordedDateUntilSeconds = behaviorData.RecordedDate?.slice(0, 19);
      const originalRecordedDateUntilSeconds = originalRecordedDate?.slice(0, 19);
      behaviorDataIndex = currentSession.BehaviorData.findIndex(
        (t) =>
          t.PatientBehaviorID === behaviorData.PatientBehaviorID &&
          ((!!t.ID && t.ID === behaviorData.ID) ||
            t.RecordedDate === recordedDateUntilSeconds ||
            (!!originalRecordedDateUntilSeconds && t.RecordedDate === originalRecordedDateUntilSeconds))
      );
    }

    if (behaviorDataIndex !== -1) {
      currentSession.BehaviorData[behaviorDataIndex] = behaviorData;

      // Save the updated session
      this.offlineDataService
        .saveOfflineSessionData(patientID, currentSession)
        .then((success) => {
          if (success) {
            this.markDirtyForSync('behavior data saved');
          }
          this.dataChanged.update((n) => n + 1);
        });
      this.dataChanged.update((n) => n + 1);
    }
  }

  // Methods to get various patient data from offline storage
  getPatientBehaviorLocations(): PatientBehaviorLocationModel[] {
    const patientID = this.getPatientID();
    if (!patientID) {
      return [];
    }

    const offlineData =
      this.offlineDataService.getOfflinePatientData(patientID);
    return offlineData?.behaviorLocations || [];
  }

  getPatientBehaviorConsequences(): PatientBehaviorConsequenceModel[] {
    const patientID = this.getPatientID();
    if (!patientID) {
      return [];
    }

    const offlineData =
      this.offlineDataService.getOfflinePatientData(patientID);
    return offlineData?.behaviorConsequences || [];
  }

  getPatientBehaviorResponses(): PatientBehaviorResponseModel[] {
    const patientID = this.getPatientID();
    if (!patientID) {
      return [];
    }

    const offlineData =
      this.offlineDataService.getOfflinePatientData(patientID);
    return offlineData?.behaviorResponses || [];
  }

  getPatientBehaviorSeverities(): PatientBehaviorSeverityModel[] {
    const patientID = this.getPatientID();
    if (!patientID) {
      return [];
    }

    const offlineData =
      this.offlineDataService.getOfflinePatientData(patientID);
    return offlineData?.behaviorSeverities || [];
  }

  getPatientBehaviorAntecedents(): PatientBehaviorAntecedentModel[] {
    const patientID = this.getPatientID();
    if (!patientID) {
      return [];
    }

    const offlineData =
      this.offlineDataService.getOfflinePatientData(patientID);
    return offlineData?.behaviorAntecedents || [];
  }

  getPatientBehaviors(): PatientBehaviorModel[] {
    const patientID = this.getPatientID();
    if (!patientID) {
      return [];
    }

    const offlineData =
      this.offlineDataService.getOfflinePatientData(patientID);
    if (!offlineData || !offlineData.behaviors) {
      return [];
    }

    return offlineData.behaviors.filter(
      (b) =>
        (b.PatientID == null || Number(b.PatientID) === patientID) &&
        !b.IsDeleted
    );
  }

  getPatientTargets(): PatientTargetModel[] {
    const patientID = this.getPatientID();
    if (!patientID) {
      return [];
    }

    const offlineData =
      this.offlineDataService.getOfflinePatientData(patientID);
    if (!offlineData || !offlineData.targets) {
      return [];
    }

    const targets = this.applyTaskAnalysisInheritedPromptCodes(
      offlineData.targets.filter(
        (target) =>
          target.PatientID == null || Number(target.PatientID) === patientID
      )
    );

    return targets.filter(
      (t) =>
        t.TargetStatus != PatientTargetStatus.Ready &&
        t.TargetStatus != PatientTargetStatus.Closed &&
        t.TargetStatus != PatientTargetStatus.Fluent &&
        t.TargetStatus != PatientTargetStatus.On_Hold &&
        !t.IsDeleted &&
        !t.IsArchived
    );
  }

  private applyTaskAnalysisInheritedPromptCodes(
    targets: PatientTargetModel[]
  ): PatientTargetModel[] {
    const targetById = new Map<number, PatientTargetModel>();

    for (const target of targets) {
      if (target.ID) {
        targetById.set(target.ID, target);
      }
    }

    return targets.map((target) => {
      if (target.TargetType !== TargetType.TaskAnalysis || !target.SubTargets?.length) {
        return target;
      }

      const inheritedPromptCodeIds = target.CustomPromptCodeIDs ?? [];
      if (!inheritedPromptCodeIds.length) {
        return target;
      }

      const inheritedPromptCodes = this.resolvePromptCodesByIds(
        inheritedPromptCodeIds,
        target.AllPromptCodes ?? target.PromptCodes ?? []
      );

      for (const subTargetReference of target.SubTargets) {
        if (!subTargetReference.ID) {
          continue;
        }

        const subTarget = targetById.get(subTargetReference.ID);
        if (!subTarget || subTarget.CustomPromptCodeIDs?.length) {
          continue;
        }

        const subTargetPromptCodes = this.resolvePromptCodesByIds(
          inheritedPromptCodeIds,
          subTarget.AllPromptCodes ?? inheritedPromptCodes
        );

        subTarget.PromptCodes = subTargetPromptCodes.length
          ? subTargetPromptCodes
          : inheritedPromptCodes;
      }

      return target;
    });
  }

  private resolvePromptCodesByIds(
    promptCodeIds: number[],
    promptCodes: PromptCodeModel[]
  ): PromptCodeModel[] {
    return promptCodes
      .filter(
        (promptCode) =>
          !!promptCode.ID &&
          promptCodeIds.includes(promptCode.ID) &&
          !promptCode.IsDeleted &&
          !promptCode.Inactive
      )
      .sort((a, b) => (a.Order ?? 0) - (b.Order ?? 0));
  }

  getSkillAreas(): SkillAreaModel[] {
    const patientID = this.getPatientID();
    if (!patientID) {
      return [];
    }

    const offlineData =
      this.offlineDataService.getOfflinePatientData(patientID);
    return offlineData?.skillAreas || [];
  }

  getPrograms(): PatientProgramModel[] {
    const patientID = this.getPatientID();
    if (!patientID) {
      return [];
    }

    const offlineData =
      this.offlineDataService.getOfflinePatientData(patientID);
    if (!offlineData || !offlineData.programs) {
      return [];
    }

    const filteredPrograms = offlineData.programs.filter(
      (p) =>
        (p.PatientID == null || Number(p.PatientID) === patientID) &&
        !p.IsDeleted &&
        !p.IsArchived &&
        p.ProgramStatus != PatientProgramStatus.Closed &&
        p.ProgramStatus != PatientProgramStatus.Ready &&
        p.ProgramStatus != PatientProgramStatus.On_Hold
    );

    // Filter targets within each program
    filteredPrograms.forEach((p) => {
      if (p.Targets) {
        let targetsFiltered = p.Targets as PatientTargetModel[];
        targetsFiltered = targetsFiltered.filter(
          (t) =>
            t.TargetStatus != PatientTargetStatus.Ready &&
            t.TargetStatus != PatientTargetStatus.Closed &&
            t.TargetStatus != PatientTargetStatus.Fluent &&
            t.TargetStatus != PatientTargetStatus.On_Hold &&
            !t.IsDeleted &&
            !t.IsArchived
        );
        p.Targets = targetsFiltered as PatientTargetModel[];
      }
    });

    return filteredPrograms;
  }

  getPlaylists(): PatientPlaylistModel[] {
    const patientID = this.getPatientID();
    if (!patientID) {
      return [];
    }

    const offlineData =
      this.offlineDataService.getOfflinePatientData(patientID);
    if (!offlineData || !offlineData.playlists) {
      return [];
    }

    return offlineData.playlists.filter(
      (p) =>
        (p.PatientID == null || Number(p.PatientID) === patientID) &&
        !p.IsDeleted
    );
  }

  getPromptSchedules(): PromptScheduleModel[] {
    const patientID = this.getPatientID();
    if (!patientID) {
      return [];
    }

    const offlineData =
      this.offlineDataService.getOfflinePatientData(patientID);
    if (!offlineData || !offlineData.promptSchedules) {
      return [];
    }

    return offlineData.promptSchedules.filter((p) => !p.IsDeleted);
  }

  getGenericPromptCodes(): PromptCodeModel[] {
    const patientID = this.getPatientID();
    if (!patientID) {
      return [];
    }

    const offlineData =
      this.offlineDataService.getOfflinePatientData(patientID);
    if (!offlineData || !offlineData.genericPromptCodes) {
      return [];
    }

    return offlineData.genericPromptCodes;
  }

  getReminders(): PatientReminderModel[] {
    const patientID = this.getPatientID();
    if (!patientID) {
      return [];
    }

    const offlineData =
      this.offlineDataService.getOfflinePatientData(patientID);
    if (!offlineData || !offlineData.reminders) {
      return [];
    }

    return offlineData.reminders.filter(
      (reminder) => !reminder.IsDeleted && !reminder.DateDeleted
    );
  }

  getPatientInstructions(): string {
    const patientID = this.getPatientID();
    if (!patientID) {
      return '';
    }

    const offlineData =
      this.offlineDataService.getOfflinePatientData(patientID);
    if (!offlineData || !offlineData.patientInstructions) {
      return '';
    }

    return offlineData.patientInstructions;
  }

  getBIPInterventions(): string[] {
    const patientID = this.getPatientID();
    if (!patientID) {
      return [];
    }

    const offlineData =
      this.offlineDataService.getOfflinePatientData(patientID);
    if (!offlineData || !offlineData.bipInterventions) {
      return [];
    }

    return offlineData.bipInterventions;
  }

  private updatePatientOfflineData(
    patientID: number,
    update: (offlineData: PatientOfflineData) => void
  ): void {
    const offlineData =
      this.offlineDataService.getOfflinePatientData(patientID);
    if (!offlineData) {
      return;
    }

    update(offlineData);
    this.offlineDataService.savePatientForOffline(patientID).subscribe();
    if (this.getPatientID() === patientID) {
      this.dataChanged.update((n) => n + 1);
    }
  }

  // Methods to refresh the data (these should be called to update the offline data)
  loadPatientBehaviorLocations(): void {
    const patientID = this.getPatientID();
    if (this.offlineDataService.isInOfflineMode() || !patientID) {
      return;
    }

    this.staffApiService
      .GetPatientBehaviorLocations()
      .subscribe((locations) => {
        this.updatePatientOfflineData(
          patientID,
          (offlineData) => (offlineData.behaviorLocations = locations)
        );
      });
  }

  loadPatientBehaviorConsequences(): void {
    const patientID = this.getPatientID();
    if (this.offlineDataService.isInOfflineMode() || !patientID) {
      return;
    }

    this.staffApiService
      .GetPatientBehaviorConsequences({ patientID })
      .subscribe((consequences) => {
        this.updatePatientOfflineData(
          patientID,
          (offlineData) => (offlineData.behaviorConsequences = consequences)
        );
      });
  }

  loadPatientBehaviorResponses(): void {
    const patientID = this.getPatientID();
    if (this.offlineDataService.isInOfflineMode() || !patientID) {
      return;
    }

    this.staffApiService
      .GetPatientBehaviorResponses()
      .subscribe((responses) => {
        this.updatePatientOfflineData(
          patientID,
          (offlineData) => (offlineData.behaviorResponses = responses)
        );
      });
  }

  loadPatientBehaviorSeverities(): void {
    const patientID = this.getPatientID();
    if (this.offlineDataService.isInOfflineMode() || !patientID) {
      return;
    }

    this.staffApiService
      .GetPatientBehaviorSeverities()
      .subscribe((severities) => {
        this.updatePatientOfflineData(
          patientID,
          (offlineData) => (offlineData.behaviorSeverities = severities)
        );
      });
  }

  loadPatientBehaviorAntecedents(): void {
    const patientID = this.getPatientID();
    if (this.offlineDataService.isInOfflineMode() || !patientID) {
      return;
    }

    this.staffApiService
      .GetPatientBehaviorAntecedents({ patientID })
      .subscribe((antecedents) => {
        this.updatePatientOfflineData(
          patientID,
          (offlineData) => (offlineData.behaviorAntecedents = antecedents)
        );
      });
  }

  loadPatientBehaviors(): void {
    const patientID = this.getPatientID();
    if (this.offlineDataService.isInOfflineMode() || !patientID) {
      return;
    }

    this.staffApiService
      .GetPatientBehaviors({
        patientID,
        lightLoad: (window as any).__sparkLightLoad === true || undefined,
      })
      .subscribe((behaviors) => {
        this.updatePatientOfflineData(
          patientID,
          (offlineData) => (offlineData.behaviors = behaviors)
        );
      });
  }

  loadPatientTargets(): void {
    const patientID = this.getPatientID();
    if (this.offlineDataService.isInOfflineMode() || !patientID) {
      return;
    }

    this.patientDataFacade
      .getTargets(patientID, {
        onlyActive: false,
      })
      .subscribe((targets: PatientTargetModel[]) => {
        this.updatePatientOfflineData(
          patientID,
          (offlineData) => (offlineData.targets = targets)
        );
      });
  }

  loadSkillAreas(): void {
    const patientID = this.getPatientID();
    if (this.offlineDataService.isInOfflineMode() || !patientID) {
      return;
    }

    this.supervisorApiService
      .GetSkillAreas({ patientID })
      .subscribe((skillAreas: SkillAreaModel[]) => {
        this.updatePatientOfflineData(
          patientID,
          (offlineData) => (offlineData.skillAreas = skillAreas)
        );
      });
  }

  loadPrograms(): void {
    const patientID = this.getPatientID();
    if (this.offlineDataService.isInOfflineMode() || !patientID) {
      return;
    }

    this.patientDataFacade
      .getPrograms(patientID, {
        onlyActive: false,
      })
      .subscribe((programs: PatientProgramModel[]) => {
        this.updatePatientOfflineData(
          patientID,
          (offlineData) => (offlineData.programs = programs)
        );
      });
  }

  loadPlaylists(): void {
    const patientID = this.getPatientID();
    if (this.offlineDataService.isInOfflineMode() || !patientID) {
      return;
    }

    this.supervisorApiService
      .GetPatientPlaylists({
        patientID,
        lightLoad: (window as any).__sparkLightLoad === true || undefined,
      })
      .subscribe((playlists: PatientPlaylistModel[]) => {
        this.updatePatientOfflineData(
          patientID,
          (offlineData) => (offlineData.playlists = playlists)
        );
      });
  }

  loadBIPImplementations(): void {
    const patientID = this.getPatientID();
    if (
      this.offlineDataService.isInOfflineMode() ||
      !patientID ||
      (window as any).__sparkLightLoad === true
    ) {
      return;
    }

    this.supervisorApiService
      .GetPatientBIPInterventions({ patientID })
      .subscribe((bipInterventions: string[]) => {
        this.updatePatientOfflineData(
          patientID,
          (offlineData) => (offlineData.bipInterventions = bipInterventions)
        );
      });
  }
  loadPromptSchedules(): void {
    const patientID = this.getPatientID();
    if (this.offlineDataService.isInOfflineMode() || !patientID) {
      return;
    }

    this.supervisorApiService
      .GetPromptSchedules({ patientID })
      .subscribe((promptSchedules: PromptScheduleModel[]) => {
        this.updatePatientOfflineData(
          patientID,
          (offlineData) => (offlineData.promptSchedules = promptSchedules)
        );
      });
  }
}
